/**
 * SF-160: desktop Overview tab content — near-verbatim port of the old Council `BuildingsTab.tsx`.
 * Read-only and self-contained (no `BuildingsShellProps` dependency), exactly like the old
 * `BuildingsTabProps`. Row hover styling reuses `DesktopBuildingsShell.tsx`'s already-injected
 * `.building-row-highlight:hover` rule rather than re-injecting a separate `<style>` tag.
 */

import { Fragment, useMemo, useState } from 'react';
import type { Province } from '../../types/province';
import { StatCard } from '../ui/StatCard';
import { BUILDING_GROUPS } from '../../constants/buildings';
import { COLORS } from '../../constants/theme';
import { formatNumber, fmtDec, pct } from '../../utils/format';
import { BUILDING_BONUS, buildPlannerData, buildExplorationPlannerData } from './buildingBonuses';

interface BuildingOverviewPanelProps {
  province: Province;
}

export function BuildingOverviewPanel({ province: p }: BuildingOverviewPanelProps) {
  const be = p.economy.buildingEfficiency;
  const totalAcres = p.territory.totals.acres;
  const built = p.territory.totals.buildings;
  const barren = p.territory.totals.barren;
  const constructionAcres = p.territory.totals.construction;
  const emp = p.economy.employment;

  const [showEmpty, setShowEmpty] = useState(false);

  const planner = useMemo(
    () => buildPlannerData(p.territory.construction),
    [p.territory.construction],
  );

  const explorationPlanner = useMemo(
    () => buildExplorationPlannerData(p.territory.exploration),
    [p.territory.exploration],
  );

  return (
    <div className="space-y-4">
      {/* ── Stat Cards ────────────────────────────────────────── */}
      <div className="grid grid-cols-5 gap-3">
        <StatCard
          label="Building Efficiency"
          value={pct(be)}
          sub="of max 100%"
        />
        <StatCard
          label="Total Built"
          value={`${formatNumber(built)} acres`}
          sub={`of ${formatNumber(totalAcres)} land`}
        />
        <StatCard
          label="Barren Land"
          value={`${formatNumber(barren)} acres`}
          sub={`${fmtDec((barren / totalAcres) * 100)}%`}
        />
        <StatCard
          label="Workers"
          value={formatNumber(emp.employed)}
          sub={`${(emp.employmentRate * 100).toFixed(0)}% employed`}
        />
        <StatCard
          label="In Construction"
          value={`${formatNumber(constructionAcres)} acres`}
          sub={`${p.territory.construction.length} orders`}
        />
      </div>

      {/* ── Current Buildings ─────────────────────────────────── */}
      <div
        className="rounded overflow-hidden"
        style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
      >
        {/* Show empty toggle */}
        <div
          className="flex items-center justify-end px-3 py-1.5"
          style={{ backgroundColor: COLORS.sectionHeader, borderBottom: `1px solid ${COLORS.border}` }}
        >
          <label className="flex items-center gap-1.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showEmpty}
              onChange={(e) => setShowEmpty(e.target.checked)}
              className="accent-current"
            />
            <span className="text-xs" style={{ color: COLORS.text.tertiary }}>Show empty buildings</span>
          </label>
        </div>

        {BUILDING_GROUPS.map((group, groupIdx) => {
          const visibleBuildings = showEmpty
            ? group.buildings
            : group.buildings.filter((b) => (p.territory.buildings[b.id] ?? 0) > 0);

          if (visibleBuildings.length === 0) return null;

          return (
            <Fragment key={group.key}>
              <div
                className="grid items-center px-3 py-1.5"
                style={{
                  gridTemplateColumns: '180px 60px 70px 16px 1fr',
                  backgroundColor: COLORS.sectionHeader,
                  borderTop: groupIdx === 0 ? undefined : `1px solid ${COLORS.border}`,
                }}
              >
                <span className="text-xs font-bold uppercase tracking-wider" style={{ color: COLORS.text.primary }}>
                  {group.label}
                </span>
                <span className="text-right text-xs uppercase tracking-wide" style={{ color: COLORS.text.tertiary }}>COUNT</span>
                <span className="text-right text-xs uppercase tracking-wide" style={{ color: COLORS.text.tertiary }}>% LAND</span>
                <span />
                <span className="text-left text-xs uppercase tracking-wide" style={{ color: COLORS.text.tertiary }}>EFFECT</span>
              </div>
              {visibleBuildings.map((building) => {
                const count = p.territory.buildings[building.id] ?? 0;
                const landPct = totalAcres > 0 ? count / totalAcres : 0;
                const isEmpty = count === 0;
                const bonusFn = BUILDING_BONUS[building.id];
                const bonus = bonusFn ? bonusFn(count, landPct, be) : '—';

                return (
                  <div
                    key={building.id}
                    className="building-row-highlight grid items-center px-3 py-1.5"
                    style={{
                      gridTemplateColumns: '180px 60px 70px 16px 1fr',
                      borderBottom: `1px solid ${COLORS.border}`,
                      opacity: isEmpty ? 0.4 : 1,
                      transition: 'background-color 0s',
                    }}
                  >
                    <span className="text-sm" style={{ color: COLORS.text.primary }}>{building.name}</span>
                    <span className="text-right font-mono text-sm" style={{ color: COLORS.text.secondary }}>{count}</span>
                    <span className="text-right font-mono text-sm" style={{ color: COLORS.text.secondary }}>{pct(landPct)}</span>
                    <span />
                    <span className="text-left text-sm font-mono" style={{ color: isEmpty ? COLORS.text.tertiary : COLORS.positive }}>
                      {isEmpty ? '—' : bonus}
                    </span>
                  </div>
                );
              })}
            </Fragment>
          );
        })}

        {/* Barren + Total */}
        <div
          className="grid items-center px-3 py-1.5"
          style={{
            gridTemplateColumns: '180px 60px 70px 16px 1fr',
            borderTop: `1px solid ${COLORS.border}`,
            borderBottom: `1px solid ${COLORS.border}`,
            opacity: barren === 0 ? 0.4 : 1,
          }}
        >
          <span className="text-sm" style={{ color: COLORS.text.primary }}>Barren Land</span>
          <span className="text-right font-mono text-sm" style={{ color: COLORS.text.secondary }}>{barren}</span>
          <span className="text-right font-mono text-sm" style={{ color: COLORS.text.secondary }}>{pct(totalAcres > 0 ? barren / totalAcres : 0)}</span>
          <span />
          <span className="text-left text-sm font-mono" style={{ color: COLORS.text.tertiary }}>—</span>
        </div>
        <div
          className="grid items-center px-3 py-1.5"
          style={{
            gridTemplateColumns: '180px 60px 70px 16px 1fr',
            backgroundColor: COLORS.sectionHeader,
          }}
        >
          <span className="text-sm font-semibold" style={{ color: COLORS.text.primary }}>Total</span>
          <span className="text-right font-mono text-sm font-semibold" style={{ color: COLORS.text.primary }}>{totalAcres}</span>
          <span className="text-right font-mono text-sm font-semibold" style={{ color: COLORS.text.primary }}>100%</span>
          <span />
          <span />
        </div>
      </div>

      {/* ── Incoming Construction ─────────────────────────────── */}
      <div
        className="rounded overflow-hidden"
        style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
      >
        <div className="px-3 py-1.5" style={{ backgroundColor: COLORS.sectionHeader }}>
          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: COLORS.text.primary }}>
            Incoming Construction
          </span>
        </div>

        {planner ? (
          <div className="overflow-x-auto">
            <table className="w-full" style={{ borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <caption className="sr-only">Incoming Construction by day</caption>
              <colgroup>
                <col style={{ width: '120px' }} />
                {planner.days.map((day) => (
                  <col key={day} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th
                    scope="col"
                    className="text-left px-2 py-1 text-[10px] uppercase tracking-wide"
                    style={{ color: COLORS.text.tertiary, borderBottom: `1px solid ${COLORS.border}` }}
                  >
                    Days until completed
                  </th>
                  {planner.days.map((day) => (
                    <th
                      key={day}
                      scope="col"
                      className="text-center py-1 text-[10px] font-mono"
                      style={{ color: COLORS.text.tertiary, borderBottom: `1px solid ${COLORS.border}` }}
                    >
                      {day}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {planner.buildings.map((b) => (
                  <tr key={b.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                    <th scope="row" className="text-left px-2 py-0.5 text-sm font-normal" style={{ color: COLORS.text.primary }}>{b.name}</th>
                    {planner.days.map((day) => {
                      const incoming = b.byDay[day];
                      return (
                        <td
                          key={day}
                          className="text-center py-0.5 font-mono text-xs"
                          style={{ color: incoming ? COLORS.text.secondary : COLORS.text.tertiary }}
                        >
                          {incoming ?? ''}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                <tr style={{ backgroundColor: COLORS.sectionHeader }}>
                  <th scope="row" className="text-left px-2 py-0.5 text-xs font-semibold" style={{ color: COLORS.text.primary }}>Total</th>
                  {planner.days.map((day) => (
                    <td
                      key={day}
                      className="text-center py-0.5 font-mono text-xs font-semibold"
                      style={{ color: COLORS.text.primary }}
                    >
                      {planner.totals[day] || ''}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-3 py-3 text-sm" style={{ color: COLORS.text.tertiary }}>
            No buildings under construction.
          </div>
        )}
      </div>

      {/* ── Incoming Exploration ──────────────────────────────── */}
      <div
        className="rounded overflow-hidden"
        style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
      >
        <div className="px-3 py-1.5" style={{ backgroundColor: COLORS.sectionHeader }}>
          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: COLORS.text.primary }}>
            Incoming Exploration
          </span>
        </div>

        {explorationPlanner ? (
          <div className="overflow-x-auto">
            <table className="w-full" style={{ borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <caption className="sr-only">Incoming Exploration by day</caption>
              <colgroup>
                <col style={{ width: '120px' }} />
                {explorationPlanner.days.map((day) => (
                  <col key={day} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th
                    scope="col"
                    className="text-left px-2 py-1 text-[10px] uppercase tracking-wide"
                    style={{ color: COLORS.text.tertiary, borderBottom: `1px solid ${COLORS.border}` }}
                  >
                    Days until completed
                  </th>
                  {explorationPlanner.days.map((day) => (
                    <th
                      key={day}
                      scope="col"
                      className="text-center py-1 text-[10px] font-mono"
                      style={{ color: COLORS.text.tertiary, borderBottom: `1px solid ${COLORS.border}` }}
                    >
                      {day}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                  <th scope="row" className="text-left px-2 py-0.5 text-sm font-normal" style={{ color: COLORS.text.primary }}>Land</th>
                  {explorationPlanner.days.map((day) => {
                    const incoming = explorationPlanner.byDay[day];
                    return (
                      <td
                        key={day}
                        className="text-center py-0.5 font-mono text-xs"
                        style={{ color: incoming ? COLORS.text.secondary : COLORS.text.tertiary }}
                      >
                        {incoming ?? ''}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-3 py-3 text-sm" style={{ color: COLORS.text.tertiary }}>
            No land being explored.
          </div>
        )}
      </div>
    </div>
  );
}
